import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { exhibitions, awards, clients } from "@/lib/data/about";

// Get base path from Vite config
const basePath = import.meta.env.BASE_URL;

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-gray-900 mb-8" style={{ fontFamily: '"Playfair Display", serif' }}>
                About Alex Chen
              </h1>
              <div className="prose prose-lg text-gray-700 space-y-6">
                <p className="leading-relaxed">
                  With over a decade of experience in professional photography,
                  I have dedicated my career to capturing authentic moments and
                  creating compelling visual narratives. My journey began with a
                  simple fascination for light and shadow, which has evolved
                  into a deep passion for storytelling through imagery.
                </p>
                <p className="leading-relaxed">
                  Born and raised in San Francisco, I discovered photography
                  during my college years while studying Fine Arts. What started
                  as a hobby quickly became my calling. I specialize in
                  portraits, landscapes, and editorial work, always seeking to
                  reveal the extraordinary in the ordinary.
                </p>
                <p className="leading-relaxed">
                  My approach combines technical precision with artistic vision,
                  creating images that not only document moments but also evoke
                  emotions and tell stories. Whether working with individual
                  clients or major publications, I bring the same level of
                  dedication and creativity to every project.
                </p>
                <p className="leading-relaxed">
                  Currently based in Los Angeles, I am available for
                  commissioned work worldwide. When I'm not behind the camera,
                  you can find me exploring new locations, mentoring emerging
                  photographers, or experimenting with new techniques in my
                  studio.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src={`${basePath}images/about/profile-about.jpg`}
                alt="Alex Chen, Professional Photographer"
                className="w-full h-auto object-cover object-top rounded-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Exhibitions */}
      <section className="bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center font-inter">
            Selected Exhibitions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {exhibitions.map((exhibition, index) => (
              <div key={index} className="bg-white p-6 rounded-sm shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 font-inter">
                      {exhibition.title}
                    </h3>
                    <p className="text-gray-700">{exhibition.venue}</p>
                  </div>
                  <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {exhibition.year}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards & Recognition */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center font-inter">
            Awards & Recognition
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {awards.map((award, index) => (
              <div
                key={index}
                className="flex items-center space-x-4 p-4 border border-gray-200 rounded-sm"
              >
                <div className="w-8 h-8 flex items-center justify-center text-yellow-600">
                  <i className="ri-award-line text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 font-inter">{award.title}</h3>
                  <p className="text-sm text-gray-500">{award.year}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Notable Clients */}
      <section className="bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center font-inter">
            Notable Clients
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {clients.map((client, index) => (
              <div
                key={index}
                className="text-center p-4 bg-white rounded-sm shadow-sm"
              >
                <p className="font-medium text-gray-700">{client}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download Portfolio */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 font-inter">
            Portfolio Download
          </h2>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
            Download my complete portfolio showcasing the best of my work across
            all categories. Perfect for galleries, clients, and collaboration
            opportunities.
          </p>
          <button
            onClick={() => {
              // Placeholder for future portfolio download functionality
            }}
            className="inline-flex items-center bg-gray-900 text-white px-8 py-3 rounded-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 mr-2 flex items-center justify-center">
              <i className="ri-download-line"></i>
            </div>
            Download Portfolio (PDF)
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
